import { TranslateService } from '@ngx-translate/core';
import mapboxgl from 'mapbox-gl';

import { ICONS } from '@core/services/icons/icons.service';
import { NotificationService } from '@core/services/notification/notification.service';

import { ContextMenuOption } from '@shared/components/context-menu/context-menu.component';
import { ContextMenuService } from '@shared/services/context-menu/context-menu.service';

import { MapElement } from '@features/components/visualization/mapbox/map-elements/map-element';

/** Decimals kept when copying a point: ~10 cm at the equator, finer than any address. */
const COORDINATE_PRECISION = 6;

/** Distinguishes the maps mounted at the same time, since the menu is routed by key. */
let nextMenuId = 0;

/**
 * Brings a longitude back into [-180, 180). Panning past the antimeridian keeps counting
 * into the next world copy, and a longitude of 200 is rejected by everything the copied
 * text is likely to be pasted into.
 */
export function wrapLongitude(lng: number): number {
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}

/** Formats a point as `lat, lng` — the order geocoders and mapping tools read on paste. */
export function formatCoordinates(point: { lng: number; lat: number }): string {
  const lat = point.lat.toFixed(COORDINATE_PRECISION);
  const lng = wrapLongitude(point.lng).toFixed(COORDINATE_PRECISION);
  return `${lat}, ${lng}`;
}

/**
 * Offers the coordinates of the point under the cursor through the right-click menu, so
 * any position on the map can be read off and pasted elsewhere.
 */
export class MapCoordinatesMenu implements MapElement {
  private readonly contextKey = `map-coordinates-${nextMenuId++}`;
  private readonly onContextMenu = (event: MouseEvent) => this.recordPoint(event);
  private container?: HTMLElement;
  private lastPoint?: { lng: number; lat: number };

  constructor(
    private readonly map: mapboxgl.Map,
    private readonly contextMenu: ContextMenuService,
    private readonly translate: TranslateService,
    private readonly notifications: NotificationService
  ) {}

  init(): void {}

  registerEvents(): void {
    this.container = this.map.getContainer();
    // The application-wide right-click handler routes on this attribute; without it the
    // map's own menu is never asked for its options.
    this.container.setAttribute('data-context', this.contextKey);
    // A DOM listener rather than the map's `contextmenu` event: Mapbox holds that event
    // back until mouseup, which lands after the menu has already collected its options.
    this.container.addEventListener('contextmenu', this.onContextMenu);
    this.contextMenu.register(this.contextKey, () => this.buildOptions());
  }

  destroy(): void {
    this.contextMenu.unregister(this.contextKey);
    this.container?.removeEventListener('contextmenu', this.onContextMenu);
    this.container?.removeAttribute('data-context');
    this.container = undefined;
    this.lastPoint = undefined;
  }

  private recordPoint(event: MouseEvent): void {
    const box = this.container!.getBoundingClientRect();
    this.lastPoint = this.map.unproject([
      event.clientX - box.left,
      event.clientY - box.top
    ]);
  }

  private buildOptions(): ContextMenuOption[] {
    if (!this.lastPoint) {
      return [];
    }

    const coordinates = formatCoordinates(this.lastPoint);
    return [
      {
        icon: ICONS.copy,
        label: this.translate.instant('map.context_menu.copy_coordinates'),
        action: () => this.copy(coordinates)
      }
    ];
  }

  private copy(coordinates: string): void {
    navigator.clipboard.writeText(coordinates).then(
      () =>
        this.notifications.success(
          this.translate.instant('map.context_menu.coordinates_copied'),
          coordinates,
          2000
        ),
      () =>
        this.notifications.error(this.translate.instant('map.context_menu.copy_error'), '')
    );
  }
}
